import React from "react";
import { Badge, Card, Table } from "react-bootstrap";
import { Link } from "react-router";
import {
  ISmartShoppingJobEvaluation,
  ISmartShoppingSession,
  ISmartShoppingTurnEvaluation,
  SMART_SHOPPING_API_LABELS,
  SmartShoppingJobSource,
  SmartShoppingKindredApi,
} from "../../interfaces";

const SOURCE: Record<SmartShoppingJobSource, { label: string; bg: string; hint: string }> = {
  api: { label: "Kindred API", bg: "success", hint: "Done with a Kindred Open Data API" },
  generic: { label: "Generic page logic", bg: "warning", hint: "Done by the extension's own store-agnostic logic, not an API" },
  ai: { label: "AI", bg: "info", hint: "Done by the model reading the page, web search, or the vision operator" },
  missing: { label: "Not done", bg: "danger", hint: "Nothing could do it" },
};

const ALL_APIS = [SmartShoppingKindredApi.ProductMap, SmartShoppingKindredApi.PromoDiscovery, SmartShoppingKindredApi.MerchantMap];

const SourceBadge: React.FC<{ j: Pick<ISmartShoppingJobEvaluation, "source" | "api"> }> = ({ j }) => (
  <Badge bg={SOURCE[j.source].bg} text={j.source === "generic" ? "dark" : undefined} title={SOURCE[j.source].hint}>
    {j.source === "api" && j.api ? SMART_SHOPPING_API_LABELS[j.api].name : SOURCE[j.source].label}
  </Badge>
);

const counts = (jobs: ISmartShoppingJobEvaluation[]) =>
  (Object.keys(SOURCE) as SmartShoppingJobSource[]).map((s) => [s, jobs.filter((j) => j.source === s).length] as const);

/** Per turn: who did each job, what AI was used, and what the APIs lacked. */
export const TurnEvaluation: React.FC<{ evaluation: ISmartShoppingTurnEvaluation }> = ({ evaluation: e }) => (
  <div className="mb-3">
    <div className="small text-muted fw-semibold mb-1">API evaluation</div>
    <div className="d-flex flex-wrap gap-2 align-items-center small mb-2">
      {ALL_APIS.map((api) => (
        <Badge
          key={api}
          bg={e.apisUsed.includes(api) ? "primary" : "light"}
          text={e.apisUsed.includes(api) ? undefined : "dark"}
          title={e.apisUsed.includes(api) ? "Used in this turn" : "Not used in this turn"}
        >
          {SMART_SHOPPING_API_LABELS[api].name} {e.apisUsed.includes(api) ? "✓" : "—"}
        </Badge>
      ))}
      <span className="text-muted">
        AI: {e.ai.llmCalls} LLM calls · {e.ai.webSearches} web searches · {e.ai.pageReads} page reads · {e.ai.visualRuns} visual runs ({e.ai.screenshots} screenshots)
        {e.ai.judged ? " · judged" : ""}
      </span>
    </div>
    {e.jobs.length > 0 && (
      <Table size="sm" responsive className="small mb-2 align-middle">
        <thead>
          <tr>
            <th>Job</th>
            <th>Done by</th>
            <th>Step result</th>
            <th>What the API lacked</th>
          </tr>
        </thead>
        <tbody>
          {e.jobs.map((j, i) => (
            <tr key={i}>
              <td className="text-nowrap">{j.job}</td>
              <td>
                <SourceBadge j={j} />
              </td>
              <td className="text-muted">{j.evidence}</td>
              <td>{j.gap ?? <span className="text-muted">—</span>}</td>
            </tr>
          ))}
        </tbody>
      </Table>
    )}
  </div>
);

/** Across the session: which APIs carried the work, how much fell back, and the distinct gaps. */
export const SessionEvaluation: React.FC<{ session: ISmartShoppingSession }> = ({ session }) => {
  const evals = session.turns.map((t) => t.evaluation).filter((e): e is ISmartShoppingTurnEvaluation => !!e);
  if (!evals.length) return null;
  const jobs = evals.flatMap((e) => e.jobs);
  const gaps = Array.from(new Set(evals.flatMap((e) => e.gaps)));
  const ai = evals.reduce(
    (a, e) => ({
      llm: a.llm + e.ai.llmCalls,
      searches: a.searches + e.ai.webSearches,
      reads: a.reads + e.ai.pageReads,
      visual: a.visual + e.ai.visualRuns,
      shots: a.shots + e.ai.screenshots,
    }),
    { llm: 0, searches: 0, reads: 0, visual: 0, shots: 0 }
  );
  return (
    <Card className="mb-3">
      <Card.Header className="py-2">
        <strong>API evaluation</strong>
        <span className="small text-muted ms-2">Who did each job across the session, read from the stored steps</span>
      </Card.Header>
      <Card.Body>
        <div className="d-flex flex-wrap gap-4 mb-3 small">
          <div>
            <div className="text-muted fw-semibold mb-1">APIs used</div>
            {ALL_APIS.map((api) => {
              const turns = evals.filter((e) => e.apisUsed.includes(api)).length;
              return (
                <div key={api}>
                  {SMART_SHOPPING_API_LABELS[api].name}: {turns}/{evals.length} turns
                </div>
              );
            })}
          </div>
          <div>
            <div className="text-muted fw-semibold mb-1">Jobs done by</div>
            {counts(jobs).map(([s, n]) => (
              <div key={s} title={SOURCE[s].hint}>
                <SourceBadge j={{ source: s, api: null }} /> {n}
              </div>
            ))}
          </div>
          <div>
            <div className="text-muted fw-semibold mb-1">Additional AI</div>
            <div>{ai.llm} LLM calls</div>
            <div>{ai.searches} web searches</div>
            <div>{ai.reads} page reads by the model</div>
            <div>
              {ai.visual} visual runs · {ai.shots} screenshots
            </div>
          </div>
        </div>
        {session.domains.length > 0 && (
          <div className="small mb-2">
            Fix a store's map:{" "}
            {session.domains.map((d) => (
              <Link key={d} to={`/auto-coupons/merchant-map-editor?domain=${encodeURIComponent(d)}&view=mobile`} className="me-2" title="Open this store's map in the merchant map editor">
                {d}
              </Link>
            ))}
          </div>
        )}
        <div className="small text-muted fw-semibold mb-1">What the APIs lacked ({gaps.length})</div>
        {gaps.length ? (
          <ul className="small mb-0">
            {gaps.map((g) => (
              <li key={g}>{g}</li>
            ))}
          </ul>
        ) : (
          <div className="small text-muted">Nothing: every job was done by a Kindred API.</div>
        )}
      </Card.Body>
    </Card>
  );
};
